/**
 * Pure duration-range planning for the "preferred durations" search (VAM-293).
 *
 * Products that only exist in a 5- or 6-night duration are invisible today: the
 * search default is one week and there is no 6-day button, so `<Durations>`
 * carries a single value that those products can never match. The plan widens
 * `<Durations>` to a window around the selected length and uses
 * `<PreferredDurations>` to keep the originally selected length (and the nearest
 * whole week) at the top of a `SortedBy=Price` result set.
 *
 * This module is deliberately free of stores, config and XML — see
 * `claude/specs/PreferredDurations/SPEC.md` §4.1 for the rules it encodes.
 */

const DAYS_PER_WEEK = 7;

/** Longest stay any product is sold in; a product maximum, not a UI preset cap. */
export const MAX_PRODUCT_DURATION = 21;

export interface DurationPlan {
  /** Every length the gateway may return, ascending. Goes into `<Durations>`. */
  durations: number[];
  /**
   * Lengths to rank first, ascending. Goes into `<PreferredDurations>` and is
   * always a subset of {@link DurationPlan.durations} — a preferred duration the
   * gateway was not also allowed to return is silently ignored.
   */
  preferred: number[];
}

/**
 * The nearest whole week for a trip length, or `null` when the length is already
 * a whole number of weeks. Never below one week: 1–3 nights round to 7, not 0.
 *
 * "Nearest", not "next higher" (Christoph, 2026-10-02): a hotel sold only in whole
 * weeks must surface its 7-night stay for an 8-night search, not the 14-night one
 * at twice the price. A tie cannot occur — no integer sits exactly half a week
 * between two multiples of 7.
 */
export function nearestWeeklyMultiple(duration: number): number | null {
  if (!Number.isFinite(duration) || duration <= 0 || duration % DAYS_PER_WEEK === 0) {
    return null;
  }
  return Math.max(DAYS_PER_WEEK, Math.round(duration / DAYS_PER_WEEK) * DAYS_PER_WEEK);
}

/**
 * Build the `<Durations>` / `<PreferredDurations>` pair for a search.
 *
 * `durations` is `[duration − flex … duration + flex]` plus the nearest whole week,
 * clamped to `[1, min(windowNights, maxLen)]`. With `flex ≥ 3` the weekly value
 * already lies inside the band for any `duration ≥ 4`, so there it only affects
 * `preferred`. Below that (a small `flex`, or 1–2 nights rounding up to 7) it can
 * sit outside the band, making the list sparse — no gaps are filled in.
 *
 * The selected length is first clamped into `[1, upper]`. Above `maxLen` it would
 * otherwise fall outside its own band, which empties `preferred` — and a request
 * with no preferred duration is the one case `SortedBy=Price` floats the shortest
 * stays to the top, the exact outcome this plan exists to prevent.
 *
 * `preferred` is `[duration, weekly]` **intersected with** `durations`. Without
 * that intersection the weekly value would be preferred but not allowed
 * whenever it lies outside the band and the window is too short for it (d=1 in
 * a 5-night window), and the gateway would drop it without a word.
 *
 * Note that a weekly value already inside the ±flex band still gets added to
 * `preferred`. That looks like a no-op but is not: it is the difference between
 * "allowed" and "ranked first".
 *
 * @param duration     the length the user selected, in nights
 * @param windowNights nights spanned by the selected date range
 * @param flex         ± offset around `duration` (`ibeSearchDefaults.durationFlex`);
 *                     `0` is the kill switch — no band AND no weekly value, so the
 *                     request stays byte-for-byte the one we send today
 * @param maxLen       product maximum, default {@link MAX_PRODUCT_DURATION}
 */
export function buildDurationPlan(
  duration: number,
  windowNights: number,
  flex: number,
  maxLen: number = MAX_PRODUCT_DURATION
): DurationPlan {
  const upper = Math.min(windowNights, maxLen);
  const safeFlex = Math.max(0, flex);
  const selected = Math.min(Math.max(duration, 1), upper);

  if (safeFlex === 0) {
    return { durations: [selected], preferred: [selected] };
  }

  const inRange = (value: number): boolean => value >= 1 && value <= upper;

  const candidates = new Set<number>();
  for (let value = selected - safeFlex; value <= selected + safeFlex; value++) {
    if (inRange(value)) {
      candidates.add(value);
    }
  }

  const weekly = nearestWeeklyMultiple(selected);
  if (weekly !== null && inRange(weekly)) {
    candidates.add(weekly);
  }

  const durations = [...candidates].sort((a, b) => a - b);
  const durationSet = new Set(durations);

  const preferred = [selected, ...(weekly !== null ? [weekly] : [])]
    .filter((value) => durationSet.has(value))
    .sort((a, b) => a - b);

  return { durations, preferred: [...new Set(preferred)] };
}
